import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import { FileValidationError } from '@aslanpay/portal-common-lib';

import FileProcessingApiHandler from 'app/services/fileProcessingApiHandler';
import {
  UploadResponsePayload,
  FileUploadResponse,
  EdfUploadAnalysisResponsePayload,
  GiftingEdfUploadAnalysisResponsePayload,
  EdfUploadType,
  toGiftingEdfAnalysisResponsePayload,
} from 'app/types/fileProcessingService';

export interface UploadEDFFileRequest {
  companyId: string;
  payCalendarId: string;
  uploadType: EdfUploadType;
  file: File;
}

export interface UploadedEDFFile {
  fileUpload: FileUploadResponse;
  analysis: EdfUploadAnalysisResponsePayload | GiftingEdfUploadAnalysisResponsePayload;
}

export interface UploadEDFFileResponse {
  uploadResponse: UploadResponsePayload;
  fileUpload: UploadedEDFFile;
}

// Real FPS service implementation
const edfFileService = {
  uploadEDFFile: async ({
    companyId,
    payCalendarId,
    uploadType,
    file,
  }: UploadEDFFileRequest): Promise<UploadEDFFileResponse> => {
    // Step 1: Get pre-signed URL for upload
    const uploadResponse = await FileProcessingApiHandler.getUploadPreSignedUrl(
      file.name,
      companyId,
      uploadType,
      payCalendarId
    );

    // Step 2: Upload file to S3 using pre-signed URL
    const uploadToS3Response = await fetch(uploadResponse.url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadToS3Response.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadToS3Response.statusText}`);
    }

    // Step 3: Request file analysis
    const rawAnalysis = await FileProcessingApiHandler.requestFileAnalysis(uploadResponse.id);

    const analysis =
      uploadType === 'GIFTING_ONLY_EMPLOYEE_DATA_FILE'
        ? toGiftingEdfAnalysisResponsePayload(rawAnalysis)
        : (rawAnalysis as EdfUploadAnalysisResponsePayload);

    // Step 4: Get the file details (for status tracking)
    const fileResponse = await FileProcessingApiHandler.getFileById(uploadResponse.id);

    const result: UploadEDFFileResponse = {
      uploadResponse,
      fileUpload: {
        fileUpload: (fileResponse.fileUpload ?? fileResponse) as FileUploadResponse,
        analysis,
      },
    };

    return result;
  },
};

interface UseUploadEDFFileOptions
  extends UseMutationOptions<UploadEDFFileResponse, Error, UploadEDFFileRequest> {
  onValidationError?: (errors: FileValidationError[]) => void;
}

const useUploadEDFFile = ({ onSuccess, onError, onValidationError }: UseUploadEDFFileOptions) => {
  return useMutation<UploadEDFFileResponse, Error, UploadEDFFileRequest>(
    (request: UploadEDFFileRequest) => edfFileService.uploadEDFFile(request),
    {
      onSuccess,
      onError: (error, variables, context) => {
        // fps's analyse step returns a structured subErrors array (e.g. missing/extra employees,
        // bad rows) rather than a plain message — surface those via onValidationError so the caller
        // can render them as a table instead of a single generic toast.
        const subErrors = (error as { response?: { data?: { subErrors?: FileValidationError[] } } })
          ?.response?.data?.subErrors;

        if (Array.isArray(subErrors) && subErrors.length > 0 && onValidationError) {
          onValidationError(subErrors);
          return;
        }

        onError?.(error, variables, context);
      },
    }
  );
};

export default useUploadEDFFile;
